import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import QRScannerDialog from "@/components/QRScannerDialog";
import { toast } from "sonner";
import {
  Loader2,
  Send,
  CheckCircle2,
  ScanLine,
  Mail,
  ShieldCheck,
  ArrowLeft,
  Download,
} from "lucide-react";
import { downloadReceipt } from "@/lib/receipt";

export default function Transfer() {
  const { user, balance, refreshBalance } = useAuth();
  const isLiveUser = user?.banking_mode === "live";
  const [mode, setMode] = useState(
    isLiveUser ? "live" : user?.banking_mode || "demo"
  );
  const [accountNumber, setAccountNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  // OTP-confirm state
  const [phase, setPhase] = useState("form"); // 'form' | 'confirm'
  const [pending, setPending] = useState(null); // { pending_id, recipient_name, requires_totp, dev_otp }
  const [emailOtp, setEmailOtp] = useState("");
  const [totpCode, setTotpCode] = useState("");

  useEffect(() => {
    if (isLiveUser && mode !== "live") setMode("live");
  }, [isLiveUser, mode]);

  const available = balance
    ? mode === "demo"
      ? balance.balance_demo
      : balance.balance_live
    : 0;
  const value = Math.max(0, Number(amount) || 0);

  useEffect(() => {
    const q = accountNumber.trim();
    if (!/^\d{10}$/.test(q)) {
      setMatch(null);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/wallet/lookup?q=${encodeURIComponent(q)}`);
        setMatch(data.found ? data : null);
      } catch {
        setMatch(null);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [accountNumber]);

  const initTransfer = async () => {
    if (!/^\d{10}$/.test(accountNumber.trim())) {
      toast.error("Enter a 10-digit account number.");
      return;
    }
    if (value <= 0) {
      toast.error("Enter an amount greater than zero.");
      return;
    }
    if (value > available) {
      toast.error("Amount exceeds your balance.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/transfer/init", {
        account_number: accountNumber.trim(),
        amount: value,
        mode,
        note: note.trim(),
      });
      setPending(data);
      setPhase("confirm");
      setEmailOtp("");
      setTotpCode("");
      if (data.dev_otp) {
        toast.warning(`Email send unavailable. Demo OTP: ${data.dev_otp}`);
      } else {
        toast.success("A confirmation code was sent to your email.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const confirmTransfer = async () => {
    if (emailOtp.length < 4) {
      toast.error("Enter the 6-digit email code.");
      return;
    }
    if (pending?.requires_totp && !totpCode.trim()) {
      toast.error("Enter your authenticator code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/transfer/confirm", {
        pending_id: pending.pending_id,
        email_otp: emailOtp.trim(),
        totp_code: pending?.requires_totp ? totpCode.trim() : null,
      });
      await refreshBalance();
      const txId = data.transaction_id;
      toast.success(`Sent $${value.toFixed(2)} successfully.`, {
        description: "Downloading your receipt…",
        duration: 5000,
      });
      // Auto-download the PDF receipt so the sender always has proof of the transfer
      if (txId) {
        downloadReceipt(txId, { silent: true });
      }
      // Reset all state
      setAccountNumber("");
      setAmount("");
      setNote("");
      setMatch(null);
      setPending(null);
      setEmailOtp("");
      setTotpCode("");
      setPhase("form");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const cancelConfirm = () => {
    setPhase("form");
    setPending(null);
    setEmailOtp("");
    setTotpCode("");
  };

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="text-eyebrow text-white/55">
          Send to another FinVault user
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-1">
          Transfer
        </h1>
        <p className="text-white/65 mt-2">
          Enter their 10-digit FinVault account number. Every transfer is
          confirmed with a one-time code sent to your email.
        </p>

        {phase === "form" ? (
          <div className="mt-8 fv-card p-6 md:p-8">
            {isLiveUser ? (
              <div
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#B58957]/15 text-[#B58957] font-medium"
                data-testid="transfer-live-only"
              >
                Live account · real money transfer
              </div>
            ) : (
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="bg-white/[0.06]">
                  <TabsTrigger value="demo" data-testid="transfer-tab-demo">
                    Demo
                  </TabsTrigger>
                  <TabsTrigger value="live" data-testid="transfer-tab-live">
                    Live
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            <div className="mt-6 flex items-center justify-between p-4 rounded-xl bg-white/[0.04] border border-white/10">
              <div className="text-sm text-white/60">Available {mode}</div>
              <div
                className="font-display text-xl font-semibold text-white"
                data-testid="transfer-available"
              >
                ${available.toFixed(2)}
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-eyebrow text-white/60">
                  Recipient account number
                </Label>
                <div className="relative mt-1">
                  <Input
                    value={accountNumber}
                    onChange={(e) =>
                      setAccountNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 10)
                      )
                    }
                    placeholder="10-digit number"
                    inputMode="numeric"
                    data-testid="transfer-account-number"
                    className="font-mono tracking-[0.25em] pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setScanOpen(true)}
                    aria-label="Scan QR"
                    data-testid="transfer-scan-button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-[#B58957] hover:bg-white/[0.06]"
                  >
                    <ScanLine className="w-4 h-4" /> Scan
                  </button>
                </div>
                {match && (
                  <div
                    className="mt-2 flex items-center gap-2 text-sm text-[#7BBF8E]"
                    data-testid="transfer-recipient-match"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Sending to{" "}
                    <strong className="ml-1">{match.name}</strong> · {match.wallet_id}
                  </div>
                )}
                {accountNumber.length === 10 && !match && (
                  <div
                    className="mt-2 text-xs text-[#E27A6A]"
                    data-testid="transfer-no-match"
                  >
                    No FinVault account with that number.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-eyebrow text-white/60">
                  Amount (USD)
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                    $
                  </span>
                  <Input
                    value={amount}
                    onChange={(e) =>
                      setAmount(e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    className="pl-8 text-2xl font-display h-14"
                    data-testid="transfer-amount"
                  />
                </div>
              </div>
              <div>
                <Label className="text-eyebrow text-white/60">
                  Note (optional)
                </Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What's it for?"
                  data-testid="transfer-note"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                onClick={initTransfer}
                disabled={loading}
                className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
                data-testid="transfer-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send ${value.toFixed(2)}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 fv-card p-6 md:p-8">
            <div className="flex items-center gap-2 text-white/85">
              <ShieldCheck className="w-5 h-5 text-[#B58957]" />
              <span className="font-display text-lg">Confirm your transfer</span>
            </div>
            <p className="text-sm text-white/60 mt-2">
              You're about to send{" "}
              <strong className="text-white">${value.toFixed(2)}</strong> to{" "}
              <strong className="text-white">{pending?.recipient_name}</strong>{" "}
              ({pending?.recipient_wallet_id}). Confirm with the codes below.
            </p>

            <div className="mt-6 space-y-5">
              <div>
                <Label className="text-xs text-white/60">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Email verification code
                </Label>
                <Input
                  value={emailOtp}
                  onChange={(e) =>
                    setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  inputMode="numeric"
                  placeholder="123456"
                  className="mt-1 font-mono tracking-[0.4em] text-center text-xl"
                  data-testid="transfer-otp-email"
                />
                {pending?.dev_otp && (
                  <p className="mt-2 text-xs text-[#B58957]">
                    Dev OTP:{" "}
                    <span className="font-mono">{pending.dev_otp}</span>
                  </p>
                )}
                <p className="mt-2 text-xs text-white/50">
                  Sent to {user?.email}. Expires in {pending?.expires_in_minutes} min.
                </p>
              </div>
              {pending?.requires_totp && (
                <div>
                  <Label className="text-xs text-white/60">
                    <ShieldCheck className="w-3 h-3 inline mr-1" />
                    Authenticator (2FA) code
                  </Label>
                  <Input
                    value={totpCode}
                    onChange={(e) =>
                      setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    inputMode="numeric"
                    placeholder="123456"
                    className="mt-1 font-mono tracking-[0.4em] text-center text-xl"
                    data-testid="transfer-otp-totp"
                  />
                  <p className="mt-2 text-xs text-white/50">
                    From your authenticator app.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 flex justify-between">
              <Button
                variant="ghost"
                onClick={cancelConfirm}
                disabled={loading}
                className="text-white/80 hover:bg-white/[0.06]"
                data-testid="transfer-otp-cancel"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button
                onClick={confirmTransfer}
                disabled={loading}
                className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
                data-testid="transfer-otp-confirm"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & send
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <QRScannerDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onDetected={(acc) => setAccountNumber(acc)}
      />
    </AppLayout>
  );
}
